/**
 * Was beim Aufnehmen eines Voiceovers eingestellt wird -- und warum.
 *
 * Der Export nimmt beim Endrender das Original und nicht die Vorschau-Datei.
 * Die Qualität einer Sprachaufnahme entscheidet sich also *bei der Aufnahme*,
 * und dort stand bisher nichts: der Rekorder lief mit der Voreinstellung des
 * Browsers, und das Mikrofon wurde mit Echounterdrückung, Rauschfilter und
 * automatischer Aussteuerung geöffnet.
 *
 * Das sind Telefonkonferenz-Einstellungen. Sie sind dafür gebaut, dass eine
 * Stimme *verständlich* durch eine schmale Leitung kommt -- nicht dafür, dass
 * sie gut klingt. Die automatische Aussteuerung zieht in Sprechpausen den
 * Raum hoch und beim nächsten Wort wieder herunter (das Pumpen), der
 * Rauschfilter schneidet mit dem Rauschen auch die Luft der Stimme weg, und
 * die Echounterdrückung ist für ein Gespräch gedacht, das hier gar nicht
 * stattfindet: der Schnitt läuft beim Aufnehmen stumm mit.
 *
 * Deshalb hier: aus als Regel, an als Schalter für den lauten Raum. Wer neben
 * einer Klimaanlage sitzt, ist mit Filter besser dran als ohne -- aber er
 * soll es entscheiden und nicht ungefragt bekommen.
 *
 * Alles in dieser Datei ist eine Entscheidung ohne Browser: sie lässt sich
 * damit prüfen, statt sie nur am Gerät auszuprobieren.
 */
/**
 * 160 kbit/s. Opus ist bei 128 für Sprache schon sehr gut, 192 ist bei einer
 * einzelnen Stimme kaum noch zu unterscheiden -- und die Aufnahme wird
 * hinterher ohnehin noch einmal umgerechnet. Die Voreinstellung des Browsers
 * lag je nach Gerät zwischen 24 und 128; das hört man.
 */
export const AUFNAHME_BITRATE = 160_000;
/** Eine Stimme, ein Kanal. Zwei gleiche Kanäle sind doppelte Daten. */
export const AUFNAHME_KANAELE = 1;
/** 48 kHz -- das Raster, in dem Opus ohnehin rechnet, und das des Exports. */
export const AUFNAHME_RATE = 48_000;
/**
 * `aufbereiten` ist der Schalter „Mikrofon aufbereiten“ -- aus als Regel.
 */
export function mikrofonWunsch(aufbereiten) {
    return {
        echoCancellation: aufbereiten,
        noiseSuppression: aufbereiten,
        autoGainControl: aufbereiten,
        channelCount: AUFNAHME_KANAELE,
        sampleRate: AUFNAHME_RATE,
    };
}
/**
 * Das Format der Aufnahme.
 *
 * Opus zuerst, wo es geht: es ist bei gleicher Größe deutlich besser als AAC
 * und auf allem außer Safari da. Safari kann kein Opus im Rekorder und gibt
 * `audio/mp4` (AAC) -- deshalb steht das daneben und nicht davor. Vorher
 * stand es davor, und damit nahm jeder Browser AAC auf, der beides kann.
 *
 * Kommt gar nichts zurück, bleibt es leer: dann entscheidet der Browser, und
 * das ist immer noch besser als eine Aufnahme, die nicht startet.
 */
export function waehleFormat(kann) {
    const reihe = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/webm'];
    return reihe.find((typ) => kann(typ)) ?? '';
}
/** Die Dateiendung zum Format, damit der Server die Datei erkennt. */
export function dateiEndung(mimeType) {
    if (/mp4|m4a|aac/.test(mimeType))
        return 'm4a';
    if (/ogg/.test(mimeType))
        return 'ogg';
    return 'webm';
}
/**
 * Der Ausschlag in dB unter der Vollaussteuerung.
 *
 * Ein Balken von 0 bis 100 % sagt nichts darüber, ob man zu leise ist: die
 * Hälfte des Ausschlags sind −6 dB, und das ist schon fast zu laut. Ohren und
 * Aussteuerungsanzeigen rechnen logarithmisch, also tut es diese auch.
 */
export function dbfs(spitze) {
    if (spitze <= 0)
        return -Infinity;
    return 20 * Math.log10(Math.min(1, spitze));
}
/** Wo der Ausschlag im Balken steht: −60 dB ganz links, 0 dB ganz rechts. */
export function balkenAnteil(spitze) {
    const db = dbfs(spitze);
    if (!Number.isFinite(db))
        return 0;
    return Math.max(0, Math.min(1, (db + 60) / 60));
}
/** Ab hier ist es zu laut, und die Aufnahme nimmt Schaden. */
export const UEBERSTEUERT_AB = 0.985;
/** Darunter hört man am Ende nichts als Rauschen. */
export const ZU_LEISE_BIS_DB = -36;
/**
 * Wie es um die Aussteuerung steht -- als Wort, nicht als Zahl.
 *
 * `hoechste` ist die lauteste Spitze, die seit dem Beginn der Aufnahme
 * gemessen wurde. Einmal übersteuert bleibt übersteuert: die Stelle ist im
 * Material, und sie geht nicht weg, weil es danach wieder leiser wurde.
 */
export function aussteuerung(jetzt, hoechste) {
    if (hoechste >= UEBERSTEUERT_AB)
        return 'uebersteuert';
    if (jetzt <= 0.0005 && hoechste <= 0.0005)
        return 'still';
    if (dbfs(hoechste) < ZU_LEISE_BIS_DB)
        return 'leise';
    return 'gut';
}
/** Der Satz dazu. Kurz, und er sagt was zu tun ist. */
export function aussteuerungSatz(stand) {
    if (stand === 'uebersteuert')
        return 'Zu laut — weiter weg vom Mikrofon, sonst knackt es.';
    if (stand === 'leise')
        return 'Sehr leise — näher heran.';
    if (stand === 'still')
        return 'Kein Ton vom Mikrofon.';
    return 'Pegel gut.';
}
/**
 * Die gehaltene Spitze als Text.
 *
 * Gerundet, und ohne das „-0 dB“, das beim Runden knapp unter der
 * Vollaussteuerung herauskommt: das liest sich wie ein Fehler und ist keiner.
 */
export function spitzeText(spitze) {
    const db = dbfs(spitze);
    if (!Number.isFinite(db))
        return '';
    const gerundet = Math.round(db);
    return `${gerundet === 0 ? 0 : gerundet} dB`;
}
//# sourceMappingURL=aufnahme.js.map